import type { Dispatch, RefObject } from 'react';
import { createContext, useContext } from 'react';

import type { FocusOverride, ParsedToken, QueryBuilderAction } from '../../types/QueryBuilder';

export type ActivePopover = {
  tokenKey: string;
  part: 'key' | 'op' | 'value' | 'type';
  /**
   * When true, the popover opens with the first option pre-highlighted so the
   * user can press Enter to commit it without first navigating with arrow keys.
   * Set by FreeTextToken when auto-creating a lozenge from a custom field name —
   * the user has already shown intent, so the very next keystroke should commit.
   */
  autoFocusFirst?: boolean;
} | null;

export interface QueryBuilderContextValue {
  query: string;
  committedQuery: string;
  parsedQuery: ParsedToken[];
  parsedCommittedQuery: ParsedToken[];
  /** Also available for mid-action use inside reducers/hooks */
  parseQuery: (query: string) => ParsedToken[];
  dispatch: Dispatch<QueryBuilderAction>;
  focusOverride: FocusOverride | null;
  wrapperRef: RefObject<HTMLDivElement | null>;
  activePopover: ActivePopover;
  setActivePopover: (popover: ActivePopover) => void;
}

const QueryBuilderContext = createContext<QueryBuilderContextValue | null>(null);

export function useQueryBuilderContext(): QueryBuilderContextValue {
  const ctx = useContext(QueryBuilderContext);
  if (!ctx) {
    throw new Error('useQueryBuilderContext must be used within a QueryBuilder');
  }
  return ctx;
}

export { QueryBuilderContext };
